export type ConnectorConfig = Record<string, unknown>;

export type ConnectorApiError = {
  message: string;
  detail: string;
  error_code: string;
};

export type ConnectorType = {
  type: string;
  displayName: string;
};

export type ConnectorTypesResponse = {
  items: ConnectorType[];
};

export type ConnectorFieldOption = {
  label: string;
  value: string;
};

export type ConnectorFieldInputType = 'text' | 'number' | 'boolean' | 'password' | 'textarea';

export type ConnectorFieldValidationRules = {
  pattern?: string;
  min?: number;
  max?: number;
};

export type ConnectorField = {
  label: string;
  inputType: ConnectorFieldInputType;
  required: boolean;
  placeholder?: string;
  validationRules?: ConnectorFieldValidationRules;
};

export type ConnectorTypeSchema = {
  type: string;
  displayName: string;
  version: string;
  fields: ConnectorField[];
};

export const CONNECTOR_STATUSES = {
  SUCCESS: 'success',
  FAILURE: 'failure',
} as const;

export type ConnectorStatus = (typeof CONNECTOR_STATUSES)[keyof typeof CONNECTOR_STATUSES] | null;

export const CONNECTOR_STATUS_FILTER_VALUES = {
  SUCCESS: CONNECTOR_STATUSES.SUCCESS,
  FAILURE: CONNECTOR_STATUSES.FAILURE,
  EMPTY: '-',
} as const;

export type ConnectorStatusFilterValue =
  (typeof CONNECTOR_STATUS_FILTER_VALUES)[keyof typeof CONNECTOR_STATUS_FILTER_VALUES];

export type ConnectorDataInfo = {
  enterprise: string;
  site: string;
  area: string;
  process: string;
  unit: string;
  component: string;
  dataName: string;
};

export type Connector = {
  id: string;
  type: string;
  name: string;
  config: ConnectorConfig;
  worker?: {
    id: string;
    name: string;
  };
  isActive: boolean;
  status: ConnectorStatus;
  dataInfo: ConnectorDataInfo;
};

export const CONNECTOR_SORT_FIELDS = {
  NAME: 'name',
  IS_ACTIVE: 'isActive',
  STATUS: 'status',
} as const;

export type ConnectorSortField = (typeof CONNECTOR_SORT_FIELDS)[keyof typeof CONNECTOR_SORT_FIELDS];

export type SortOrder = 'asc' | 'desc';

export type ConnectorListParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  isActive?: boolean;
  status?: ConnectorStatusFilterValue[];
  sortBy?: ConnectorSortField;
  sortOrder?: SortOrder;
};

export type ConnectorListResponse = {
  items: Connector[];
  page: {
    total: number;
    page: number;
    pageSize: number;
  };
  summary: {
    enable: number;
    disable: number;
    success: number;
    failure: number;
  };
};

export type CreateConnectorRequest = {
  name: string;
  type: string;
  config: ConnectorConfig;
  dataInfo: ConnectorDataInfo;
};

export type CreateConnectorResponse = Connector;

export const CONNECTOR_RUNNING_STATUSES = {
  RUNNING: 'running',
  STOPPED: 'stopped',
} as const;

export type ConnectorRunningStatus = (typeof CONNECTOR_RUNNING_STATUSES)[keyof typeof CONNECTOR_RUNNING_STATUSES];

export type UpdateConnectorsStatusRequest = {
  ids: string[];
  status: ConnectorRunningStatus;
};

export type DeleteConnectorsRequest = {
  ids: string[];
};

export type UpdateConnectorRequest = {
  name: string;
  type: string;
  config: ConnectorConfig;
  dataInfo: ConnectorDataInfo;
};
